import type { MxfRewrapItem, MxfRewrapItemProvider } from '@mimir/common/transcoding/mxf/rewrapInputs';
import type { BackendClient } from '@mimir/common/util/backendClient';
import { api_getItem } from './api';
import { TechnicalVideoDataField, getTechnicalMetadataValue } from '@mimir/common/util/technicalMetadata';
import type { FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import { divideFuzzy, integerToFuzzy } from '@mimir/common/util/fuzzyMath';
import { getFrameDurationFromFrameRateApproximation } from '@mimir/common/util/framerate';
import { PromiseUtil } from '@mimir/common/async/async';
import type { LocalMediaInfo, LocalMediaPathProvider } from '@mimir/common/types/localMediaPathProvider';
import type { ResourceLocation } from '@mimir/common/io/resource';
import { HighresUrlProviderWithClient } from './itemUrlProvider';
import { makeStaticUrlProvider } from '@mimir/common/io/urlProvider';

export class DefaultMxfRewrapItemProvider implements MxfRewrapItemProvider {
  constructor(
    private backendClient: BackendClient,
    private localMediaPathProvider: LocalMediaPathProvider | null,
  ) { }

  async getRewrapItems(itemIds: string[]): Promise<MxfRewrapItem[]> {
    const promiseUtil = new PromiseUtil<MxfRewrapItem>();
    for (const itemId of itemIds) {
      promiseUtil.push(this.fetchRewrapItem(itemId));
    }
    return await promiseUtil.waitForAll();
  }

  private async fetchRewrapItem(
    itemId: string
  ): Promise<MxfRewrapItem> {
    const item = await api_getItem(this.backendClient, itemId);

    const approximateFrameRate = getTechnicalMetadataValue<number>(item.technicalMetadata, TechnicalVideoDataField.FrameRate);
    if (approximateFrameRate === null) {
      throw new Error(`Item ${item.id} is not compatible with MXF rewrap: no frame rate`);
    }
    let origin = getTechnicalMetadataValue<number>(item.technicalMetadata, TechnicalVideoDataField.MxfOrigin);
    if (origin === null) {
      origin = 0;
    }

    const frameDuration : FuzzyValue = getFrameDurationFromFrameRateApproximation(approximateFrameRate);
    const frameRate = divideFuzzy(integerToFuzzy(1), frameDuration);

    let highRes : ResourceLocation | null = null;

    if (item.highRes !== undefined) {
      highRes = {
        type: 'http',
        provider: new HighresUrlProviderWithClient(itemId, item.highRes, this.backendClient),
      };
    }

    if (this.localMediaPathProvider !== null) {
      const localMediaInfo : LocalMediaInfo = {
        type: 'highres',
        itemId: item.id,
        ingest: item.ingest ?? null,
        ingestSourceFullPath: item.ingestSourceFullPath ?? null,
        ingestSourceS3Bucket: item.ingestSourceS3Bucket ?? null,
        originalFileName: item.originalFileName ?? null,
      };

      const localPath = await this.localMediaPathProvider.getLocalMediaPath(localMediaInfo);
      if (localPath !== null) {
        highRes = { type: 'file', filename: localPath };
      }
    }

    if (highRes === null) {
      throw new Error(`Item ${item.id} is not compatible with MXF rewrap: no high res URL and media is not found on local disk.`);
    }

    let indexLocation : ResourceLocation | null = null;
    if (item.indexUrl !== undefined) {
      indexLocation = { type: 'http', provider: makeStaticUrlProvider(item.indexUrl) };
    }


    if (this.localMediaPathProvider !== null) {
      const localIndexInfo : LocalMediaInfo = {
        type: 'index',
        itemId: item.id,
      };
      const localIndexPath = await this.localMediaPathProvider.getLocalMediaPath(localIndexInfo);
      if (localIndexPath !== null) {
        indexLocation = { type: 'file', filename: localIndexPath };
      }
    }

    if (indexLocation === null) {
      throw new Error(`Item ${item.id} is not compatible with MXF rewrap: no index URL and index file is not found on local disk.`);
    }

    const mxfRewrapItem : MxfRewrapItem = {
      id: item.id,
      highRes,
      index: indexLocation,
      frameRate,
      segmentDuration: item.media?.segmentDuration ?? null,
      compatibilityHash: item.rewrapCompatibilityHash ?? null,
      origin: origin,
    };

    return mxfRewrapItem;
  }
}

